import React, { useState, useEffect } from 'react';
import { 
  Box, 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Icon, 
  Drawer, 
  Card, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText,
  Badge,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  ListItemAvatar,
  Avatar,
  Chip,
  ListSubheader,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { remapAppearanceAlertUrl } from '../../utils/appearanceAlertUrls';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import HouseIcon from '@mui/icons-material/House';
import PeopleIcon from '@mui/icons-material/People';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SettingsIcon from '@mui/icons-material/Settings';
import DescriptionIcon from '@mui/icons-material/Description';
import MessageIcon from '@mui/icons-material/Message';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CloseIcon from '@mui/icons-material/Close';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import InventoryIcon from '@mui/icons-material/Inventory';
import PaidIcon from '@mui/icons-material/Paid';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import ScienceIcon from '@mui/icons-material/Science';
import GavelIcon from '@mui/icons-material/Gavel';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';

import './ManagetNav.scss';
import AuthApi from '../../api/AuthApi';
import ManagerDto from '../../interfaces/ManagerDto';
import ManagerAlertApi, { ManagerAlertDto } from '../../api/ManagerAlertApi';
import SendUpdateDialog from '../SendUpdateDialog/SendUpdateDialog';

const ManagerNav = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [sendUpdateOpen, setSendUpdateOpen] = useState(false);
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [alertAnchorEl, setAlertAnchorEl] = useState<null | HTMLElement>(null);
  const [alerts, setAlerts] = useState<ManagerAlertDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [canViewPayments, setCanViewPayments] = useState(false);

  const loadManagerPermissions = async () => {
    try {
      const manager = await AuthApi.managerMe();
      if (manager && !('error' in manager)) {
        setCanViewPayments((manager as ManagerDto).Manager_CanViewPayments === true);
      }
    } catch (error) {
      console.error('Failed to load manager permissions:', error);
    }
  };

  // Load alerts on component mount and periodically
  useEffect(() => {
    loadManagerPermissions();
    loadAlerts();
    
    // Refresh alerts every 30 seconds
    const interval = setInterval(loadAlerts, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const loadAlerts = async () => {
    try {
      setLoading(true);
      const activeAlerts = await ManagerAlertApi.getManagerAlerts({ ManagerAlert_Active: true });
      setAlerts(activeAlerts);
    } catch (error) {
      console.error('Failed to load alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAlertsClick = (event: React.MouseEvent<HTMLElement>) => {
    setAlertAnchorEl(event.currentTarget);
    setAlertsOpen(true);
  };

  const handleAlertsClose = () => {
    setAlertAnchorEl(null);
    setAlertsOpen(false);
  };

  const handleAlertClick = async (alert: ManagerAlertDto) => {
    try {
      // Dismiss the alert
      await ManagerAlertApi.dismissAlert(alert.ManagerAlert_guid);
      
      // Navigate to the alert URL
      if (alert.ManagerAlert_Url) {
        navigate(remapAppearanceAlertUrl(alert.ManagerAlert_Url));
      }
      
      // Refresh alerts and close menu
      await loadAlerts();
      handleAlertsClose();
    } catch (error) {
      console.error('Failed to handle alert click:', error);
    }
  };

  const handleDismissAlert = async (alertGuid: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent triggering the alert click
    
    try {
      await ManagerAlertApi.dismissAlert(alertGuid);
      await loadAlerts();
    } catch (error) {
      console.error('Failed to dismiss alert:', error);
    }
  };

  const handleDismissAll = async () => {
    try {
      await ManagerAlertApi.dismissAllAlerts();
      await loadAlerts();
      handleAlertsClose();
    } catch (error) {
      console.error('Failed to dismiss all alerts:', error);
    }
  };

  const Logout = () => {
    AuthApi.logout().then(() => {
      window.location.pathname = '/login';
    }).catch((error) => {
      console.error("Logout failed", error);
    });
  }

  const refreshOnDrawerClose = () => {
    loadManagerPermissions();
  };

  return (
    <Box className="nav">
      <AppBar position="static" className="nav-nav">

        <Toolbar className="nav-nav-tb">
          <Link to="/">
            <img src="/images/txTicketDrLogo.png" className="nav-nav-tb-a-icn" />
          </Link>
          
          <Box sx={{ flexGrow: 1 }} />

          <Button color="inherit" startIcon={<MessageIcon />} onClick={() => setSendUpdateOpen(true)}>
            Send update
          </Button>
          
          {/* Notification Bell */}
          {alerts.length > 0 && (
            <IconButton 
              color="inherit" 
              onClick={handleAlertsClick}
              sx={{ marginRight: 1 }}
            >
              <Badge badgeContent={alerts.length} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          )}
          
          <Button color="inherit" onClick={() => setOpen(true)}>
            <MenuIcon />
          </Button>
        </Toolbar>
      </AppBar>
      <SendUpdateDialog open={sendUpdateOpen} onClose={() => setSendUpdateOpen(false)} />
      <>
        {/* Main Navigation Drawer */}
        <Drawer
          anchor={'right'}
          open={open}
          onClose={() => {
            setOpen(false);
            refreshOnDrawerClose();
          }}
        >
          <div className="nav-drawer">
            <Typography variant="h4" className="nav-drawer-ttl">Menu</Typography>
            <List className="nav-drawer-list">
              
              <ListItem disablePadding>
                <ListItemButton component={Link} to="/">
                  <ListItemIcon>
                    <HouseIcon />
                  </ListItemIcon>
                  <ListItemText primary={'Home'} />
                </ListItemButton>
              </ListItem>
              {/*
                Open / closed cases (Lloyd, Aug 7). These replace the old "Tickets" entry, which
                pointed at a search page whose Active/Inactive dropdown read a legacy flag nothing
                ever sets — it reported every case as active. /tickets still resolves for old links.
              */}
              <ListItem disablePadding>
                <ListItemButton component={Link} to="/cases/open">
                  <ListItemIcon>
                    <FolderOpenIcon />
                  </ListItemIcon>
                  <ListItemText primary={'Open Cases'} />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton component={Link} to="/cases/closed">
                  <ListItemIcon>
                    <CheckCircleOutlineIcon />
                  </ListItemIcon>
                  <ListItemText primary={'Closed Cases'} />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton component={Link} to="/users">
                  <ListItemIcon>
                    <PeopleIcon />
                  </ListItemIcon>
                  <ListItemText primary={'Clients'} />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton component={Link} to="/managers">
                  <ListItemIcon>
                    <SupervisorAccountIcon />
                  </ListItemIcon>
                  <ListItemText primary={'Managers'} />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton component={Link} to="/courts">
                  <ListItemIcon>
                    <AccountBalanceIcon />
                  </ListItemIcon>
                  <ListItemText primary={'Courts'} />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton component={Link} to="/products">
                  <ListItemIcon>
                    <InventoryIcon />
                  </ListItemIcon>
                  <ListItemText primary={'Products'} />
                </ListItemButton>
              </ListItem>
              {canViewPayments && (
                <ListItem disablePadding>
                  <ListItemButton component={Link} to="/revenue">
                    <ListItemIcon>
                      <PaidIcon />
                    </ListItemIcon>
                    <ListItemText primary={'Revenue'} />
                  </ListItemButton>
                </ListItem>
              )}
              <ListItem disablePadding>
                <ListItemButton component={Link} to="/calendar">
                  <ListItemIcon>
                    <CalendarTodayIcon />
                  </ListItemIcon>
                  <ListItemText primary={'Calendar'} />
                </ListItemButton>
              </ListItem>
              <ListSubheader disableSticky sx={{ bgcolor: 'transparent' }}>
                Discovery
              </ListSubheader>
              <ListItem disablePadding>
                <ListItemButton component={Link} to="/discovery/overdue-acknowledgments">
                  <ListItemIcon>
                    <AssignmentLateIcon />
                  </ListItemIcon>
                  <ListItemText primary={'Overdue acknowledgments'} />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton component={Link} to="/discovery/expert-designation-heads-up">
                  <ListItemIcon>
                    <ScienceIcon />
                  </ListItemIcon>
                  <ListItemText primary={'Expert designation'} />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton component={Link} to="/trial/awaiting-settings">
                  <ListItemIcon>
                    <GavelIcon />
                  </ListItemIcon>
                  <ListItemText primary={'Waiting on a trial setting'} />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton component={Link} to="/covering-attorneys">
                  <ListItemIcon>
                    <SupervisorAccountIcon />
                  </ListItemIcon>
                  <ListItemText primary={'Covering attorneys'} />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton component={Link} to="/stale-cases">
                  <ListItemIcon>
                    <ClearAllIcon />
                  </ListItemIcon>
                  <ListItemText primary={'Waiting on a court'} />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton component={Link} to="/cdl-requests">
                  <ListItemIcon>
                    <ClearAllIcon />
                  </ListItemIcon>
                  <ListItemText primary={'CDL requests'} />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton component={Link} to="/reminder-drafts">
                  <ListItemIcon>
                    <ClearAllIcon />
                  </ListItemIcon>
                  <ListItemText primary={'Reminders due'} />
                </ListItemButton>
              </ListItem>
              <ListSubheader disableSticky sx={{ bgcolor: 'transparent' }}>
                Agreements
              </ListSubheader>
              <ListItem disablePadding>
                <ListItemButton component={Link} to="/agreements/pending">
                  <ListItemIcon>
                    <AssignmentIndIcon />
                  </ListItemIcon>
                  <ListItemText primary={'Pending addendums'} />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton component={Link} to="/bulk-notices">
                  <ListItemIcon>
                    <UploadFileIcon />
                  </ListItemIcon>
                  <ListItemText primary={'Upload notices'} />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton component={Link} to="/messages">
                  <ListItemIcon>
                    <MessageIcon />
                  </ListItemIcon>
                  <ListItemText primary={'Messages'} />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton component={Link} to="/alerts">
                  <ListItemIcon>
                    <NotificationsIcon />
                  </ListItemIcon>
                  <ListItemText primary={'Alerts'} />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton component={Link} to="/letters">
                  <ListItemIcon>
                    <DescriptionIcon />
                  </ListItemIcon>
                  <ListItemText primary={'Letters'} />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton component={Link} to="/settings">
                  <ListItemIcon>
                    <SettingsIcon />
                  </ListItemIcon>
                  <ListItemText primary={'Settings'} />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={()=>Logout()} component={Link} to="/">
                  <ListItemIcon>
                    <LogoutIcon />
                  </ListItemIcon>
                  <ListItemText primary={'Log Out'} />
                </ListItemButton>
              </ListItem>
            </List>
          </div>
        </Drawer>

        {/* Alerts Menu */}
        <Menu
          anchorEl={alertAnchorEl}
          open={alertsOpen}
          onClose={handleAlertsClose}
          PaperProps={{
            sx: {
              width: 400,
              maxHeight: 500,
              overflow: 'auto'
            }
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <Box sx={{ p: 2, borderBottom: '1px solid #ddd' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                Notifications ({alerts.length})
              </Typography>
              {alerts.length > 0 && (
                <Button 
                  size="small" 
                  startIcon={<ClearAllIcon />}
                  onClick={handleDismissAll}
                >
                  Dismiss All
                </Button>
              )}
            </Box>
          </Box>
          
          {alerts.length === 0 ? (
            <MenuItem disabled>
              <Typography variant="body2" color="text.secondary">
                No active notifications
              </Typography>
            </MenuItem>
          ) : (
            alerts.map((alert, index) => (
              <MenuItem
                key={alert.ManagerAlert_guid}
                onClick={() => handleAlertClick(alert)}
                sx={{
                  p: 2,
                  borderBottom: index < alerts.length - 1 ? '1px solid #f0f0f0' : 'none',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                    '& .dismiss-button': {
                      opacity: 1,
                    }
                  }
                }}
              >
                <Box sx={{ width: '100%', position: 'relative' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {alert.ManagerAlert_Title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ 
                    mb: 1,
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    whiteSpace: 'pre-wrap',
                    maxWidth: '320px'
                  }}>
                    {alert.ManagerAlert_Desc}
                  </Typography>
                  <Chip 
                    size="small" 
                    label={new Date(alert.ManagerAlert_CreatedAt).toLocaleDateString('en-US')} 
                    variant="outlined"
                  />
                  
                  <IconButton
                    size="small"
                    className="dismiss-button"
                    onClick={(e) => handleDismissAlert(alert.ManagerAlert_guid, e)}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      opacity: 0,
                      transition: 'opacity 0.2s',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      }
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </MenuItem>
            ))
          )}
        </Menu>
      </>
    </Box>
  );
}
export default ManagerNav;